import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTheme } from '@app/theme/ThemeContext';
import { useWeatherPreferences } from './useWeatherPreferences';
import { getModelColor } from '../utils/colorUtils';

interface Location {
  lat: number;
  lon: number;
  name: string;
}

export function useForecastFavoritesWithWeatherPreferences() {
  const { theme } = useTheme();
  const {
    getWeatherPreferences,
    updateCustomModelColors,
    updateFavoriteWeatherVariables,
    updateFavoriteWeatherModels,
    updateFavoriteCities,
    removeFavoriteCity,
    isSyncing,
    error,
    generateId,
    addOrUpdateCityIfNotExists,
  } = useWeatherPreferences();

  const [preferences, setPreferences] = useState({
    customModelColors: {} as Record<string, string>,
    favoriteWeatherVariables: [] as string[],
    favoriteWeatherModels: {} as Record<string, { name: string }>,
    favoriteCities: {} as Record<string, { name: string; lat: number; lon: number }>,
  });

  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  // Load preferences on mount only
  useEffect(() => {
    const loadPreferences = async () => {
      const prefs = await getWeatherPreferences();
      setPreferences(prefs);
      setIsInitialLoadComplete(true);
    };
    loadPreferences();
  }, [getWeatherPreferences]); // Only run on mount

  // Convert weather preferences to Sets for compatibility with existing forecast code
  const favoriteVariables = useMemo(() => {
    return new Set(preferences.favoriteWeatherVariables);
  }, [preferences.favoriteWeatherVariables]);

  const favoriteModels = useMemo(() => {
    return new Set(Object.keys(preferences.favoriteWeatherModels));
  }, [preferences.favoriteWeatherModels]);

  const favoriteLocations = useMemo(() => {
    return Object.values(preferences.favoriteCities);
  }, [preferences.favoriteCities]);

  // Update favorite variables
  const setFavoriteVariables = useCallback(
    async (variables: Set<string>) => {
      const variableArray = Array.from(variables);

      // Update local state immediately (optimistic)
      const updatedPrefs = {
        ...preferences,
        favoriteWeatherVariables: variableArray,
      };
      setPreferences(updatedPrefs);

      // Update backend
      await updateFavoriteWeatherVariables(variableArray);
    },
    [preferences, updateFavoriteWeatherVariables],
  );

  // Update favorite models
  const setFavoriteModels = useCallback(
    async (models: Set<string>) => {
      const modelArray = Array.from(models);

      // Build new models object, preserving existing colors/names
      const newModels: Record<string, { name: string }> = {};
      for (const modelId of modelArray) {
        if (preferences.favoriteWeatherModels[modelId]) {
          // Keep existing model data
          newModels[modelId] = preferences.favoriteWeatherModels[modelId];
        } else {
          // Create new model entry
          newModels[modelId] = { name: modelId.toUpperCase() };
        }
      }

      // Update local state immediately (optimistic)
      const updatedPrefs = {
        ...preferences,
        favoriteWeatherModels: newModels,
      };
      setPreferences(updatedPrefs);

      // Update backend
      await updateFavoriteWeatherModels(newModels);
    },
    [preferences, updateFavoriteWeatherModels],
  );

  // Set model color
  const setModelColor = useCallback(
    async (modelId: string, color: string) => {
      // Update local state immediately (optimistic)
      const updatedColors = { ...preferences.customModelColors };
      if (color === getModelColor(modelId, 0, theme)) {
        delete updatedColors[modelId];
      } else {
        updatedColors[modelId] = color;
      }
      const updatedPrefs = {
        ...preferences,
        customModelColors: updatedColors,
      };
      setPreferences(updatedPrefs);

      // Update backend
      await updateCustomModelColors(updatedColors);
    },
    [preferences, updateCustomModelColors, theme],
  );

  // Add favorite location
  const addFavoriteLocation = useCallback(
    async (location: Location) => {
      // Check if location already exists
      const isAlreadyFavorite = Object.values(preferences.favoriteCities).some(
        (city) => city.name === location.name,
      );

      // If not already a favorite, add it optimistically to local state
      if (!isAlreadyFavorite) {
        const cityId = generateId();
        const updatedCities = {
          ...preferences.favoriteCities,
          [cityId]: {
            name: location.name,
            lat: location.lat,
            lon: location.lon,
          },
        };

        const updatedPrefs = {
          ...preferences,
          favoriteCities: updatedCities,
        };
        setPreferences(updatedPrefs);
      }

      // Update backend (this will handle duplicates and sync properly)
      const result = await addOrUpdateCityIfNotExists(location);

      // If there was an error or unexpected result, refresh from backend to ensure consistency
      if (!result.wasAdded && !isAlreadyFavorite) {
        const updatedPrefs = await getWeatherPreferences();
        setPreferences(updatedPrefs);
      }
    },
    [addOrUpdateCityIfNotExists, getWeatherPreferences, preferences, generateId],
  );

  // Remove favorite location
  const removeFavoriteLocation = useCallback(
    async (locationName: string) => {
      // Find the city ID by name
      const cityEntry = Object.entries(preferences.favoriteCities).find(
        ([, city]) => city.name === locationName,
      );

      if (cityEntry) {
        const [cityId] = cityEntry;

        // Update local state immediately (optimistic)
        const updatedCities = { ...preferences.favoriteCities };
        delete updatedCities[cityId];

        const updatedPrefs = {
          ...preferences,
          favoriteCities: updatedCities,
        };
        setPreferences(updatedPrefs);

        // Update backend
        await removeFavoriteCity(cityId);
      }
    },
    [preferences, removeFavoriteCity],
  );

  // Check if location is favorite
  const isFavoriteLocation = useCallback(
    (locationName: string) => {
      return Object.values(preferences.favoriteCities).some((city) => city.name === locationName);
    },
    [preferences.favoriteCities],
  );

  // Backward compatibility - provide setFavoriteLocations for existing code
  const setFavoriteLocations = useCallback(
    async (locations: Location[]) => {
      // Build new cities object
      const newCities: Record<string, { name: string; lat: number; lon: number }> = {};
      for (const location of locations) {
        const cityId = generateId();
        newCities[cityId] = {
          name: location.name,
          lat: location.lat,
          lon: location.lon,
        };
      }

      // Update local state immediately (optimistic)
      const updatedPrefs = {
        ...preferences,
        favoriteCities: newCities,
      };
      setPreferences(updatedPrefs);

      // Update backend
      await updateFavoriteCities(newCities);
    },
    [preferences, updateFavoriteCities, generateId],
  );

  return {
    // Core data
    favoriteVariables,
    setFavoriteVariables,
    favoriteModels,
    setFavoriteModels,
    favoriteLocations,
    setFavoriteLocations,

    // Location helpers
    addFavoriteLocation,
    removeFavoriteLocation,
    isFavoriteLocation,

    // Model colors
    customModelColors: preferences.customModelColors,
    setModelColor,

    // For backward compatibility
    modelColors: preferences.favoriteWeatherModels,
    setModelColors: setModelColor, // Note: this doesn't match the original signature but provides similar functionality

    // Loading and error states
    isLoading: isSyncing,
    isInitialLoadComplete,
    error,
  };
}
